import {
  Component,
  OnDestroy,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
  untracked
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import {
  GeoAreaEditSessionService,
  NEW_AREA_UID
} from '@core/services/geo-area-edit-session/geo-area-edit-session.service';
import { ICONS } from '@core/services/icons/icons.service';
import { SiteContextService } from '@core/services/site-context/site-context.service';
import {
  GeoJsonPolygon,
  countPointsInside,
  distinctVertexCount,
  hasHoles,
  outerRing,
  polygonAreaKm2,
  ringToSvgPath,
  validatePolygon
} from '@core/utils/geo-polygon.utils';

import { FormBuilderBase } from '@shared/components/row-edition-form/form-builder/form-builder.model';

const THUMB_WIDTH = 56;
const THUMB_HEIGHT = 40;

@Component({
  selector: 'app-geojson-polygon',
  imports: [TranslatePipe],
  templateUrl: './geojson-polygon.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeojsonPolygonComponent),
      multi: true
    },
    // Without NG_VALIDATORS the validate() below is never called, and an invalid
    // geometry would reach the backend with Save still enabled.
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GeojsonPolygonComponent),
      multi: true
    }
  ]
})
export class GeojsonPolygonComponent implements ControlValueAccessor, Validator, OnDestroy {
  protected readonly ICONS = ICONS;
  protected readonly thumbWidth = THUMB_WIDTH;
  protected readonly thumbHeight = THUMB_HEIGHT;

  private readonly session = inject(GeoAreaEditSessionService);
  private readonly siteContext = inject(SiteContextService);

  id = input<FormBuilderBase['id']>();
  entityUid = input<string>('');

  protected readonly polygon = signal<GeoJsonPolygon | null>(null);
  protected readonly isDisabled = signal(false);

  protected readonly problem = computed(() => validatePolygon(this.polygon()));
  protected readonly isEmpty = computed(() => this.polygon() === null);
  /**
   * Emptiness is owned by the generic required validator that every field type gets, so a
   * blank optional geometry must not be reported as this field's own error — neither to the
   * form nor to the reader.
   */
  protected readonly hasError = computed(() => {
    const problem = this.problem();
    return problem !== null && problem !== 'empty';
  });

  protected readonly thumbnailPath = computed(() => {
    const current = this.polygon();
    return current ? ringToSvgPath(outerRing(current), THUMB_WIDTH, THUMB_HEIGHT) : '';
  });

  protected readonly vertexCount = computed(() => {
    const current = this.polygon();
    return current ? distinctVertexCount(outerRing(current)) : 0;
  });

  protected readonly areaKm2 = computed(() => {
    const current = this.polygon();
    return current ? Math.round(polygonAreaKm2(current) * 10) / 10 : 0;
  });

  /** A geometry with interior rings cannot be drawn without discarding them. */
  protected readonly isReadOnly = computed(() => {
    const current = this.polygon();
    return current !== null && hasHoles(current);
  });

  protected readonly coveredSites = computed(() => {
    const current = this.polygon();
    if (!current) {
      return 0;
    }
    return countPointsInside(
      this.siteContext.displayedItems().map((site) => site.coords),
      current
    );
  });

  /**
   * Counts describe the backdrop currently on the map, so they disappear with it rather
   * than reporting a coverage the reader cannot see.
   */
  protected readonly loadedSites = computed(() => this.siteContext.displayedItems().length);
  protected readonly totalSites = computed(() =>
    this.siteContext.isShown() ? this.siteContext.available() : 0
  );

  private onChange: (value: GeoJsonPolygon | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      const fromMap = this.session.geometry();
      if (!this.session.isActive()) {
        return;
      }
      untracked(() => {
        if (fromMap !== this.polygon()) {
          this.polygon.set(fromMap);
          this.onChange(fromMap);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.session.end();
  }

  writeValue(value: GeoJsonPolygon | null): void {
    this.polygon.set(value ?? null);
  }

  registerOnChange(fn: (value: GeoJsonPolygon | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  /**
   * The form-builder renders a field error as `'form.error.' + <first error key>`, handing
   * the error's *value* to the translate pipe as interpolation params — so the problem has
   * to be carried by the key, not by the value. ngx-translate runs a non-empty string value
   * through JSON.parse and throws from inside change detection, once per pass, for as long
   * as the invalid geometry is on screen.
   */
  validate(): Record<string, true> | null {
    switch (this.problem()) {
      case 'self-intersecting':
        return { geoPolygonSelfIntersecting: true };
      case 'too-few-vertices':
        return { geoPolygonTooFewVertices: true };
      default:
        // 'empty' included: emptiness belongs to the generic required validator.
        return null;
    }
  }

  protected onEditOnMap(): void {
    this.onTouched();
    this.session.begin({
      uid: this.entityUid() || NEW_AREA_UID,
      geometry: this.polygon(),
      label: this.entityUid() || undefined,
      onCommit: (geometry) => this.applyFromMap(geometry),
      onCancel: (geometry) => this.applyFromMap(geometry)
    });
  }

  protected onClear(): void {
    this.onTouched();
    this.applyFromMap(null);
  }

  private applyFromMap(geometry: GeoJsonPolygon | null): void {
    this.polygon.set(geometry);
    this.onChange(geometry);
  }
}
